/**
 * @deprecated Use src/api/axiosClient.ts for public CMTS API access.
 * Internal compatibility export retained during route/service migration.
 */
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig, type Method } from "axios";
import { CMTS_API } from "../core/api/cmtsApiPaths.js";
import type { ApiResponse } from "../core/api/cmtsContractTypes.js";
import { CmtsApiError, normalizeErrorEnvelope, type CmtsErrorEnvelope } from "../core/api/cmtsApiErrors.js";
import { cmtsEnv } from "../config/env.config.js";

export type ApiFieldError = {
  readonly field: string;
  readonly message: string;
  readonly code?: string | undefined;
};

export type ApiDependencyError = {
  readonly entity?: string | undefined;
  readonly id?: string | undefined;
  readonly message: string;
  readonly code?: string | undefined;
};

export type ApiErrorResponse = {
  readonly errorCode: string;
  readonly message: string;
  readonly correlationId?: string | undefined;
  readonly traceId?: string | undefined;
  readonly fieldErrors: readonly ApiFieldError[];
  readonly dependencyErrors: readonly ApiDependencyError[];
  readonly remediation?: string | undefined;
};

export type NormalizedApiError = ApiErrorResponse & {
  readonly httpStatus: number;
  readonly method?: string | undefined;
  readonly url?: string | undefined;
  readonly isNetworkError: boolean;
  readonly isTimeout: boolean;
  readonly isCancelled: boolean;
  readonly isPermissionBlocked: boolean;
  readonly isConflict: boolean;
  readonly isValidationError: boolean;
  readonly envelope: CmtsErrorEnvelope;
};

export type CmtsAuthTokenSet = {
  readonly accessToken?: string | undefined;
  readonly refreshToken?: string | undefined;
  readonly expiresAtUtc?: string | undefined;
};

export type CmtsRequestOptions = {
  readonly headers?: Record<string, string> | undefined;
  readonly query?: Record<string, string | number | boolean | null | undefined> | undefined;
  readonly signal?: AbortSignal | undefined;
  readonly idempotencyKey?: string | undefined;
  readonly skipRefresh?: boolean | undefined;
  readonly responseType?: NonNullable<AxiosRequestConfig["responseType"]> | undefined;
  readonly onUploadProgress?: AxiosRequestConfig["onUploadProgress"] | undefined;
  readonly onDownloadProgress?: AxiosRequestConfig["onDownloadProgress"] | undefined;
  /** Overrides the global Axios timeout for this request only. */
  readonly timeoutMs?: number | undefined;
};

export type CmtsApiDiagnosticsEvent = {
  readonly path: string;
  readonly method: string;
  readonly status?: number | undefined;
  readonly correlationId: string;
  readonly error?: CmtsApiError | undefined;
};

export type CmtsHttpClientOptions = {
  readonly baseUrl: string;
  readonly timeoutMs?: number | undefined;
  readonly getToken?: (() => string | undefined) | undefined;
  readonly refreshToken?: (() => Promise<string | undefined>) | undefined;
  readonly getTenantCode?: (() => string | undefined) | undefined;
  readonly onUnauthorized?: ((error: CmtsApiError) => void | Promise<void>) | undefined;
  readonly onForbidden?: ((error: CmtsApiError) => void | Promise<void>) | undefined;
  readonly onConflict?: ((error: CmtsApiError) => void | Promise<void>) | undefined;
  readonly onDiagnostics?: ((event: CmtsApiDiagnosticsEvent) => void) | undefined;
};

export type CmtsApiInterceptorBindings = {
  readonly getAccessToken?: (() => string | undefined) | undefined;
  readonly refreshToken?: (() => Promise<string | undefined>) | undefined;
  readonly getTenantCode?: (() => string | undefined) | undefined;
  readonly onUnauthorized?: ((error: CmtsApiError) => void | Promise<void>) | undefined;
  readonly onForbidden?: ((error: CmtsApiError) => void | Promise<void>) | undefined;
  readonly onConflict?: ((error: CmtsApiError) => void | Promise<void>) | undefined;
  readonly onDiagnostics?: ((event: CmtsApiDiagnosticsEvent) => void) | undefined;
};

type RetryAwareAxiosConfig = InternalAxiosRequestConfig & {
  cmtsSkipRefresh?: boolean | undefined;
  cmtsRetryAttempted?: boolean | undefined;
  cmtsIdempotencyKey?: string | undefined;
  cmtsCorrelationId?: string | undefined;
  cmtsTimeoutMs?: number | undefined;
};

type AxiosRequestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

type ParsedCmtsResponse<T> = {
  readonly ok: true;
  readonly status: number;
  readonly data: T;
  readonly message: string;
  readonly correlationId?: string | undefined;
} | {
  readonly ok: false;
  readonly status: number;
  readonly error: CmtsApiError;
};

let apiBindings: CmtsApiInterceptorBindings = {};
let requestInterceptorId: number | undefined;
let responseInterceptorId: number | undefined;

export const cmtsApi: AxiosInstance = axios.create({
  baseURL: cmtsEnv.CMTS_API_BASE_URL,
  timeout: cmtsEnv.CMTS_API_TIMEOUT_MS,
});

configureApiInterceptors({});

export function configureApiInterceptors(bindings: CmtsApiInterceptorBindings): void {
  apiBindings = bindings;
  if (requestInterceptorId !== undefined) {
    cmtsApi.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = undefined;
  }
  if (responseInterceptorId !== undefined) {
    cmtsApi.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = undefined;
  }

  requestInterceptorId = cmtsApi.interceptors.request.use((config) => applyRequestPolicy(config as RetryAwareAxiosConfig));
  responseInterceptorId = cmtsApi.interceptors.response.use(
    (response) => response,
    async (error: unknown) => handleAxiosResponseError(error),
  );
}

export function configureCmtsApiService(input: { readonly baseUrl?: string | undefined; readonly timeoutMs?: number | undefined; readonly bindings?: CmtsApiInterceptorBindings | undefined }): void {
  const normalizedBase = normalizeApiBaseUrl(input.baseUrl ?? cmtsEnv.CMTS_API_BASE_URL);
  cmtsApi.defaults.baseURL = normalizedBase;
  cmtsApi.defaults.timeout = input.timeoutMs ?? cmtsEnv.CMTS_API_TIMEOUT_MS;
  if (axios.defaults.adapter !== undefined) cmtsApi.defaults.adapter = axios.defaults.adapter;
  if (input.bindings) configureApiInterceptors(input.bindings);
}

export function createCorrelationId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") return cryptoObj.randomUUID();
  return `cmts-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function createIdempotencyKey(prefix = "cmts-command"): string {
  return `${prefix}-${createCorrelationId()}`;
}

export function normalizeApiBaseUrl(baseUrl: string): string {
  const trimmed = baseUrl.trim().replace(/\/+$/, "");
  if (!trimmed) throw new Error("CMTS_API_BASE_URL_REQUIRED");
  return trimmed.replace(/(\/api\/cmts)+$/i, "/api/cmts");
}

export function normalizeApiPath(path: string): string {
  const [rawPath = "", query = ""] = path.trim().split("?", 2);
  const withoutOrigin = rawPath.replace(/^https?:\/\/[^/]+/i, "");
  let normalized = withoutOrigin.replace(/\\/g, "/").replace(/\/+/g, "/");
  normalized = `/${normalized.replace(/^\/+/, "")}`;
  normalized = normalized.replace(/^\/api\/cmts(?=\/|$)/i, "");
  if (!normalized || normalized === "/") normalized = "/";
  const finalPath = normalized.startsWith("/") ? normalized : `/${normalized}`;
  return query ? `${finalPath}?${query}` : finalPath;
}

export function normalizeApiUrl(baseUrl: string, path: string): string {
  const normalizedBase = normalizeApiBaseUrl(baseUrl);
  const normalizedPath = normalizeApiPath(path);
  const [pathOnly, query] = normalizedPath.split("?", 2);
  const joined = `${normalizedBase}/${(pathOnly ?? "/").replace(/^\/+/, "")}`.replace(/([^:]\/)\/+/g, "$1");
  const result = query ? `${joined}?${query}` : joined;
  return result.replace(/\/api\/cmts\/api\/cmts/gi, "/api/cmts");
}

export function isApiCancellationError(error: unknown): boolean {
  if (axios.isCancel(error)) return true;
  if (error instanceof CmtsApiError) return error.envelope.kind === "ABORTED";
  if (error instanceof DOMException && error.name === "AbortError") return true;
  if (error instanceof Error && /aborted|canceled|cancelled/i.test(error.message)) return true;
  if (isAxiosError(error)) return error.code === "ERR_CANCELED";
  return false;
}

export function normalizeApiError(error: unknown): NormalizedApiError {
  if (error instanceof CmtsApiError) return fromEnvelope(error.envelope, undefined, undefined, error.envelope.status);
  if (isAxiosError(error)) {
    const status = error.response?.status ?? (error.code === "ECONNABORTED" ? 0 : 0);
    const responseData = error.response?.data;
    const method = error.config?.method?.toUpperCase();
    const url = error.config?.url;
    const headerCorrelationId = readAxiosHeader(error.response, "x-correlation-id") ?? readAxiosHeader(error.response, "X-Correlation-Id");
    const traceId = readAxiosHeader(error.response, "x-trace-id") ?? readAxiosHeader(error.response, "X-Trace-Id");
    const cancellation = isApiCancellationError(error);
    const timeout = error.code === "ECONNABORTED" || /timeout/i.test(error.message);
    const envelope = normalizeErrorEnvelope(
      enrichErrorBody(responseData, headerCorrelationId, traceId),
      cancellation ? 0 : status,
      cancellation ? "CMTS request was cancelled." : timeout ? "CMTS request timed out." : error.message || "CMTS API request failed.",
    );
    return fromEnvelope(
      {
        ...envelope,
        kind: cancellation ? "ABORTED" : timeout ? "TIMEOUT" : envelope.kind,
        correlationId: envelope.correlationId ?? headerCorrelationId,
      },
      method,
      url,
      status,
      timeout,
      cancellation,
    );
  }
  const envelope = normalizeErrorEnvelope(error, 0, error instanceof Error && error.message ? error.message : "CMTS API request failed.");
  return fromEnvelope(envelope, undefined, undefined, envelope.status);
}

export async function apiGet<T>(path: string, options?: CmtsRequestOptions): Promise<T> {
  return requestJson<T>("GET", path, undefined, options);
}

export async function apiPost<T>(path: string, body?: unknown, options?: CmtsRequestOptions): Promise<T> {
  return requestJson<T>("POST", path, body, options);
}

export async function apiPut<T>(path: string, body?: unknown, options?: CmtsRequestOptions): Promise<T> {
  return requestJson<T>("PUT", path, body, options);
}

export async function apiPatch<T>(path: string, body?: unknown, options?: CmtsRequestOptions): Promise<T> {
  return requestJson<T>("PATCH", path, body, options);
}

export async function apiDelete<T>(path: string, options?: CmtsRequestOptions): Promise<T> {
  return requestJson<T>("DELETE", path, undefined, options);
}

export async function apiUpload<T>(path: string, form: FormData, options?: CmtsRequestOptions): Promise<T> {
  return requestJson<T>("POST", path, form, { ...options, headers: { ...(options?.headers ?? {}) }, responseType: "json" }, true);
}

export async function apiDownload(path: string, options?: CmtsRequestOptions & { readonly body?: unknown | undefined; readonly method?: "GET" | "POST" | undefined }): Promise<Blob> {
  const method = options?.method ?? (options?.body === undefined ? "GET" : "POST");
  const response = await requestRaw<Blob>(method, path, options?.body, { ...options, responseType: "blob" });
  return response.data instanceof Blob ? response.data : new Blob([response.data as BlobPart]);
}

export class CmtsHttpClient {
  constructor(private readonly options: CmtsHttpClientOptions) {
    if (!options.baseUrl || !options.baseUrl.trim()) throw new Error("CMTS_API_BASE_URL_REQUIRED");
    configureCmtsApiService({
      baseUrl: options.baseUrl,
      timeoutMs: options.timeoutMs,
      bindings: {
        getAccessToken: options.getToken,
        refreshToken: options.refreshToken,
        getTenantCode: options.getTenantCode,
        onUnauthorized: options.onUnauthorized,
        onForbidden: options.onForbidden,
        onConflict: options.onConflict,
        onDiagnostics: options.onDiagnostics,
      },
    });
  }

  get<T>(path: string, options?: CmtsRequestOptions): Promise<T> { return this.request<T>("GET", path, undefined, options); }
  post<T>(path: string, body?: unknown, options?: CmtsRequestOptions): Promise<T> { return this.request<T>("POST", path, body, options); }
  put<T>(path: string, body?: unknown, options?: CmtsRequestOptions): Promise<T> { return this.request<T>("PUT", path, body, options); }
  patch<T>(path: string, body?: unknown, options?: CmtsRequestOptions): Promise<T> { return this.request<T>("PATCH", path, body, options); }
  delete<T>(path: string, options?: CmtsRequestOptions): Promise<T> { return this.request<T>("DELETE", path, undefined, options); }
  upload<T>(path: string, form: FormData, options?: CmtsRequestOptions): Promise<T> { return apiUpload<T>(path, form, options); }
  download(path: string, options?: CmtsRequestOptions): Promise<Blob> { return apiDownload(path, options); }
  downloadPost(path: string, body?: unknown, options?: CmtsRequestOptions): Promise<Blob> { return apiDownload(path, { ...options, method: "POST", body }); }

  private async request<T>(method: AxiosRequestMethod, path: string, body?: unknown, options?: CmtsRequestOptions): Promise<T> {
    try {
      return await requestJson<T>(method, path, body, options);
    } catch (error: unknown) {
      const apiError = error instanceof CmtsApiError ? error : new CmtsApiError(normalizeApiError(error).envelope);
      const correlationId = apiError.envelope.correlationId ?? createCorrelationId();
      this.options.onDiagnostics?.({ path, method, status: apiError.envelope.status, correlationId, error: apiError });
      if (apiError.envelope.kind === "UNAUTHORIZED") await this.options.onUnauthorized?.(apiError);
      if (apiError.envelope.kind === "FORBIDDEN") await this.options.onForbidden?.(apiError);
      if (apiError.envelope.kind === "CONFLICT") await this.options.onConflict?.(apiError);
      throw apiError;
    }
  }
}

async function requestJson<T>(method: AxiosRequestMethod, path: string, body?: unknown, options?: CmtsRequestOptions, isForm = false): Promise<T> {
  const response = await requestRaw<unknown>(method, path, body, { ...options, responseType: options?.responseType ?? "json" }, isForm);
  const parsed = parseAxiosResponse<T>(response);
  if (!parsed.ok) throw parsed.error;
  return parsed.data;
}

async function requestRaw<T>(method: AxiosRequestMethod, path: string, body?: unknown, options?: CmtsRequestOptions, isForm = false): Promise<AxiosResponse<T>> {
  const headers: Record<string, string> = { ...(options?.headers ?? {}) };
  if (headers["Cache-Control"] === undefined) headers["Cache-Control"] = "no-store";
  if (headers.Pragma === undefined) headers.Pragma = "no-cache";
if (!isForm && body !== undefined) {
    headers["Content-Type"] = "application/json";
  }  const config: AxiosRequestConfig = {
    method,
    url: normalizeApiPath(path),
    data: body,
    headers,
    timeout: options?.timeoutMs ?? cmtsApi.defaults.timeout ?? cmtsEnv.CMTS_API_TIMEOUT_MS,
    responseType: options?.responseType ?? "json",
  };
  if (options?.query) config.params = options.query;
  if (options?.signal) config.signal = options.signal;
  if (options?.onUploadProgress) config.onUploadProgress = options.onUploadProgress;
  if (options?.onDownloadProgress) config.onDownloadProgress = options.onDownloadProgress;
  if (options?.idempotencyKey) {
    (config as AxiosRequestConfig & { cmtsIdempotencyKey?: string }).cmtsIdempotencyKey = options.idempotencyKey;
  }
  if (options?.skipRefresh) {
    (config as AxiosRequestConfig & { cmtsSkipRefresh?: boolean }).cmtsSkipRefresh = true;
  }
  if (options?.timeoutMs !== undefined) {
    (config as AxiosRequestConfig & { cmtsTimeoutMs?: number }).cmtsTimeoutMs = options.timeoutMs;
  }
  return cmtsApi.request<T>(config);
}

function parseAxiosResponse<T>(response: AxiosResponse<unknown>): ParsedCmtsResponse<T> {
  const status = response.status;
  const body = response.data;
  const correlationId = readAxiosHeader(response, "x-correlation-id") ?? readAxiosHeader(response, "X-Correlation-Id");

  if (body === undefined || body === null || body === "") {
    return { ok: true, status, data: undefined as T, message: "No content", correlationId };
  }

  const envelope = isRecord(body) ? body as Partial<ApiResponse<T>> : undefined;
  const hasSuccessFlag = typeof envelope?.success === "boolean";
  if (hasSuccessFlag && envelope?.success === false) {
    return { ok: false, status, error: new CmtsApiError(normalizeErrorEnvelope(body, status, envelope.message || "CMTS API returned a failure envelope")) };
  }

  const data = hasSuccessFlag ? envelope?.data as T : body as T;
  return { ok: true, status, data, message: envelope?.message ?? "OK", correlationId: envelope?.correlationId ?? correlationId };
}

function applyRequestPolicy(config: RetryAwareAxiosConfig): RetryAwareAxiosConfig {
  const method = (config.method ?? "GET").toUpperCase();
  const headers = config.headers;
  config.url = normalizeApiPath(config.url ?? "/");
  config.timeout = config.cmtsTimeoutMs ?? cmtsApi.defaults.timeout ?? cmtsEnv.CMTS_API_TIMEOUT_MS;

  const token = apiBindings.getAccessToken?.();
  if (!isLoginRequest(config.url) && token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const correlationId = config.cmtsCorrelationId ?? readHeaderFromConfig(config, "X-Correlation-Id") ?? createCorrelationId();
  config.cmtsCorrelationId = correlationId;
  headers.set("X-Correlation-Id", correlationId);

  const tenantCode = apiBindings.getTenantCode?.() ?? cmtsEnv.CMTS_DEFAULT_TENANT_CODE;
  if (tenantCode) headers.set("X-Tenant-Code", tenantCode);

  headers.set("X-Client-App-Version", cmtsEnv.CMTS_CLIENT_APP_VERSION);

  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const idempotencyKey = config.cmtsIdempotencyKey ?? readHeaderFromConfig(config, "Idempotency-Key");
    if (idempotencyKey) headers.set("Idempotency-Key", idempotencyKey);
  }
  return config;
}

async function handleAxiosResponseError(error: unknown): Promise<AxiosResponse<unknown>> {
  if (!isAxiosError(error)) throw new CmtsApiError(normalizeApiError(error).envelope);
  const config = error.config as RetryAwareAxiosConfig | undefined;
  const status = error.response?.status ?? 0;
  const normalized = normalizeApiError(error);
  const apiError = new CmtsApiError(normalized.envelope);
  const path = config?.url ?? "unknown";
  const method = (config?.method ?? "GET").toUpperCase();
  const correlationId = normalized.correlationId ?? config?.cmtsCorrelationId ?? createCorrelationId();

  if (
    status === 401 &&
    config &&
    !config.cmtsSkipRefresh &&
    !config.cmtsRetryAttempted &&
    !isRefreshRequest(config.url) &&
    !isLoginRequest(config.url)
  ) {
    const refreshedToken = await apiBindings.refreshToken?.();
    if (refreshedToken) {
      const retryConfig: RetryAwareAxiosConfig = { ...config, cmtsRetryAttempted: true };
      retryConfig.headers = config.headers;
      retryConfig.headers.set("Authorization", `Bearer ${refreshedToken}`);
      try {
        return await cmtsApi.request(retryConfig);
      } catch (retryError: unknown) {
        if (isAxiosError(retryError) || retryError instanceof CmtsApiError) throw retryError;
        throw new CmtsApiError(normalizeApiError(retryError).envelope);
      }
    }
    await apiBindings.onUnauthorized?.(apiError);
  }

  if (status === 403) await apiBindings.onForbidden?.(apiError);
  if (status === 409) await apiBindings.onConflict?.(apiError);
  if (!normalized.isCancelled) apiBindings.onDiagnostics?.({ path, method, status, correlationId, error: apiError });
  throw apiError;
}

function fromEnvelope(envelope: CmtsErrorEnvelope, method?: string, url?: string, statusOverride?: number, timeout = false, cancelled = false): NormalizedApiError {
  const status = statusOverride ?? envelope.status;
  return {
    errorCode: envelope.code ?? codeFromStatus(status, cancelled, timeout),
    message: envelope.message,
    correlationId: envelope.correlationId,
    traceId: stringFromRecord(envelope.details, "traceId"),
    fieldErrors: fieldErrorsFromEnvelope(envelope),
    dependencyErrors: dependencyErrorsFromEnvelope(envelope),
    remediation: typeof envelope.details?.remediation === "string" ? envelope.details.remediation : undefined,
    httpStatus: status,
    method,
    url,
    isNetworkError: envelope.kind === "NETWORK" || status === 0,
    isTimeout: timeout || envelope.kind === "TIMEOUT",
    isCancelled: cancelled || envelope.kind === "ABORTED",
    isPermissionBlocked: status === 403 || envelope.kind === "FORBIDDEN",
    isConflict: status === 409 || envelope.kind === "CONFLICT",
    isValidationError: status === 400 || status === 422 || envelope.kind === "VALIDATION",
    envelope,
  };
}

function fieldErrorsFromEnvelope(envelope: CmtsErrorEnvelope): ApiFieldError[] {
  const fromErrors = envelope.errors.filter((item) => item.field).map((item) => ({ field: item.field ?? "", message: item.message, code: item.code })).filter((item) => item.field);
  if (fromErrors.length > 0) return fromErrors;
  const rawFieldErrors = readRecordArray(envelope.raw, "fieldErrors");
  return rawFieldErrors.map((item) => ({ field: readString(item.field, "general"), message: readString(item.message, envelope.message), code: optionalString(item.code) }));
}

function dependencyErrorsFromEnvelope(envelope: CmtsErrorEnvelope): ApiDependencyError[] {
  const rawDependencyErrors = readRecordArray(envelope.raw, "dependencyErrors");
  return rawDependencyErrors.map((item) => ({ entity: optionalString(item.entity), id: optionalString(item.id), message: readString(item.message, envelope.message), code: optionalString(item.code) }));
}

function enrichErrorBody(input: unknown, correlationId?: string, traceId?: string): unknown {
  if (!isRecord(input)) return input;
  const details = isRecord(input.details) ? input.details : {};
  return {
    ...input,
    correlationId: optionalString(input.correlationId) ?? correlationId,
    details: { ...details, traceId: optionalString(input.traceId) ?? traceId },
  };
}

function codeFromStatus(status: number, cancelled: boolean, timeout: boolean): string {
  if (cancelled) return "CMTS_REQUEST_CANCELLED";
  if (timeout) return "CMTS_REQUEST_TIMEOUT";
  if (status === 401) return "CMTS_UNAUTHORIZED";
  if (status === 403) return "CMTS_FORBIDDEN";
  if (status === 404) return "CMTS_NOT_FOUND";
  if (status === 409) return "CMTS_CONFLICT";
  if (status === 422 || status === 400) return "CMTS_VALIDATION_ERROR";
  if (status >= 500) return "CMTS_SERVER_ERROR";
  return "CMTS_API_ERROR";
}

function isRefreshRequest(url: string | undefined): boolean {
  return Boolean(url && /\/auth\/refresh(?:\?|$)/i.test(normalizeApiPath(url)));
}

function isLoginRequest(url: string | undefined): boolean {
  return Boolean(url && /\/auth\/(?:login|otp\/verify)(?:\?|$)/i.test(normalizeApiPath(url)));
}

function readAxiosHeader(response: AxiosResponse<unknown> | undefined, name: string): string | undefined {
  const value = response?.headers?.[name] ?? response?.headers?.[name.toLowerCase()];
  return typeof value === "string" && value.trim() ? value : undefined;
}

function readHeaderFromConfig(config: RetryAwareAxiosConfig, name: string): string | undefined {
  const value = config.headers.get(name);
  return typeof value === "string" && value.trim() ? value : undefined;
}

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readRecordArray(input: unknown, key: string): Record<string, unknown>[] {
  if (!isRecord(input)) return [];
  const value = input[key];
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function readString(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function stringFromRecord(record: Record<string, unknown> | undefined, key: string): string | undefined {
  return record ? optionalString(record[key]) : undefined;
}

export { CMTS_API, CmtsApiError, type CmtsErrorEnvelope };